import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import Cookies from 'js-cookie';
import { camelCase, snakeCase, isArray, transform, isObject } from 'lodash';
import { OLD_SESSION_STORE_NAME, SESSION_STORE_NAME } from 'context/Session';
import { getLocaleFromStorage } from 'hooks';
import { environment } from 'utils/environmentVariables';
import { DEEPLINK_STORAGE_KEY, KYC_COOKIE_NAME } from '../constants';
import { AUTH_TOKEN_STORE_NAME } from './authTokenApi';

const changeCasing = (
  obj: Record<string, unknown>,
  modifier: (string: string) => string
) =>
  transform(
    obj,
    (result: Record<string, unknown>, value: unknown, key: string, target) => {
      const camelKey = isArray(target)
        ? key
        : key == key.toUpperCase()
        ? modifier(key).toUpperCase()
        : modifier(key);
      result[camelKey] = isObject(value)
        ? changeCasing(value as Record<string, unknown>, modifier)
        : value;
    }
  );

type ErrorCode =
  | 'COMPLETED_PREVIEW'
  | 'DELETED_PREVIEW'
  | 'COMPLETED_BUY_ORDER'
  | 'INTERNAL_SERVER_ERROR'
  | 'INSUFFICIENT_FUNDS'
  | 'OBSOLETE_PREVIEW'
  | 'NOT_AVAILABLE'
  | 'PAYMENT_FAILED'
  | 'POLL_FAILURE';

export interface GeneralErrorResponse {
  errorCode: ErrorCode;
  reason: string;
}

export interface FormError {
  parameter: string;
  reason: string;
  errorCode: string;
  localizedReason: string;
}
export interface FormErrorResponse {
  errorCode: 'FORM_VALIDATION_FAILED';
  formErrors: FormError[];
}

export type ErrorResponse = { tracingId?: string; statusCode?: string } & (
  | GeneralErrorResponse
  | FormErrorResponse
);

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: Record<string, unknown>;
  dontModifyCasing?: boolean;
};

const pushDeepLink = () => {
  const currentDeeplinks: string[] = JSON.parse(
    window.sessionStorage.getItem(DEEPLINK_STORAGE_KEY) ?? '[]'
  ) as string[];
  window.sessionStorage.setItem(
    DEEPLINK_STORAGE_KEY,
    JSON.stringify([
      ...currentDeeplinks,
      window.location.pathname + window.location.search + window.location.hash,
    ])
  );
};

/**
 * A wrapper function for making API calls
 * Can be use to modify the fetch requests, modify headers etc etc globally
 * We can add a second optional argument options of type object to achieve the same
 *
 * In the case of an error the BE will response it will either:
 * - ErrorResponse as above. Then it's a "known" error in the BE.
 * - Not Json, empty json or a json with {details}.
 *
 * For the latter case it's a more internal error and we don't want to expose
 * that to the end user.
 *
 * We always have snake_case in the BE and camelCase in the FE. Hence we need to
 * change the casing.
 */
export const fetchAPI = async <T>(
  url: string,
  options?: RequestOptions,
  token?: string
): Promise<T> => {
  let data = null;
  let status = null;

  const BASE_URL = environment.REACT_APP_BASE_URL;
  const sessionToken = token
    ? token
    : Capacitor.isNativePlatform()
    ? (await Preferences.get({ key: AUTH_TOKEN_STORE_NAME })).value
    : null;

  const authHeader: HeadersInit = sessionToken
    ? { Authorization: `Bearer ${sessionToken}` }
    : {};
  const headers: HeadersInit = {
    ...options?.headers,
    ...authHeader,
    'Content-Type': 'application/json',
    'Accept-Language': getLocaleFromStorage(),
    'x-csrftoken': Cookies.get('appapi-csrftoken') ?? '',
    'x-app-readonly': environment.READONLY ? 'true' : 'false',
  };
  try {
    const response = await fetch(`${BASE_URL ? BASE_URL + '/' : ''}${url}`, {
      ...options,
      body: options?.body
        ? JSON.stringify(
            options.dontModifyCasing
              ? options.body
              : changeCasing(options.body, snakeCase)
          )
        : undefined,
      headers: headers,
      credentials: 'include',
    });
    status = response.status;
    if (response.status === 204) {
      return null as unknown as T;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    data = changeCasing(await response.json(), camelCase);
    if (response.ok) {
      return data as T;
    }
    if (status === 401) {
      await Preferences.remove({ key: AUTH_TOKEN_STORE_NAME });
      await Preferences.remove({ key: SESSION_STORE_NAME });
      localStorage.removeItem(OLD_SESSION_STORE_NAME);
      pushDeepLink();
      if (window.location.pathname.includes('/onramp')) {
        window.location.replace('/onramp/login' + location.search);
      } else if (window.location.pathname.includes('/processing')) {
        window.location.replace('/processing/login' + location.search);
      } else {
        window.location.replace('/login' + location.search);
      }
    }
    if (status === 403 && 'kyc' in data) {
      pushDeepLink();
      Cookies.set(KYC_COOKIE_NAME, JSON.stringify(data.kyc));
      if (window.location.pathname.includes('/onramp')) {
        window.location.assign('/onramp/onboarding/steps');
      } else {
        window.location.assign('/onboarding/steps');
      }
    }
    if (!('errorCode' in data)) throw Error('Generic error');
  } catch (exception) {
    console.error('fetchAPI exception', exception);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw {
      errorCode: 'INTERNAL_SERVER_ERROR',
      reason: 'A server error occurred.',
      statusCode: `${status}`,
    } as ErrorResponse;
  }
  if (!('localizedReason' in data)) {
    data['localizedReason'] = data.errorCode;
  }
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw data as unknown as ErrorResponse;
};
